import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Item Variants
// ---------------------------------------------------------------------------

const intuiItemVariants = cva(
  'flex items-center gap-3 rounded-lg transition-colors',
  {
    variants: {
      variant: {
        default:
          'text-foreground',
        outline:
          'border border-border bg-transparent text-foreground',
        muted:
          'bg-muted/50 text-foreground',
      },
      size: {
        default: 'px-3 py-2.5 text-sm',
        sm: 'px-3 py-2.5 text-sm',
        xs: 'px-3 py-2 text-xs',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

// ---------------------------------------------------------------------------
// IntuiItem
// ---------------------------------------------------------------------------

export interface IntuiItemProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof intuiItemVariants> {
  /** Leading slot — icon, avatar, image, or any ReactNode */
  media?: React.ReactNode
  /** Primary text */
  title: string
  /** Secondary text below the title */
  description?: string
  /** Trailing slot — button, icon, or any ReactNode */
  action?: React.ReactNode
  /** Whether the item is in a selected state */
  selected?: boolean
}

const IntuiItem = React.forwardRef<HTMLDivElement, IntuiItemProps>(
  (
    {
      className,
      variant,
      size,
      media,
      title,
      description,
      action,
      selected,
      onClick,
      onKeyDown,
      ...props
    },
    ref
  ) => {
    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> | undefined =
      onClick
        ? (e) => {
            onKeyDown?.(e)
            if (!e.defaultPrevented && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault()
              e.currentTarget.click()
            }
          }
        : onKeyDown

    return (
      <div
        ref={ref}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        aria-pressed={onClick && selected != null ? selected : undefined}
        aria-current={!onClick && selected ? true : undefined}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        className={cn(
          intuiItemVariants({ variant, size }),
          onClick && 'cursor-pointer hover:bg-accent',
          selected && 'ring-2 ring-ring',
          className
        )}
        {...props}
      >
        {media && (
          <div className="flex-shrink-0" data-testid="item-media">
            {media}
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <span
            className={cn(
              'truncate font-medium leading-tight',
              size === 'xs' ? 'text-xs' : 'text-sm'
            )}
            data-testid="item-title"
          >
            {title}
          </span>
          {description && (
            <span
              className={cn(
                'truncate text-muted-foreground',
                size === 'xs' ? 'text-[11px]' : 'text-xs'
              )}
              data-testid="item-description"
            >
              {description}
            </span>
          )}
        </div>

        {action && (
          <div className="flex-shrink-0" data-testid="item-action">
            {action}
          </div>
        )}
      </div>
    )
  }
)
IntuiItem.displayName = 'IntuiItem'

// ---------------------------------------------------------------------------
// IntuiItemGroup
// ---------------------------------------------------------------------------

export interface IntuiItemGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Show dividers between items */
  dividers?: boolean
}

const IntuiItemGroup = React.forwardRef<HTMLDivElement, IntuiItemGroupProps>(
  ({ className, dividers = true, children, ...props }, ref) => {
    const items = React.Children.toArray(children)

    return (
      <div
        ref={ref}
        role="list"
        className={cn('flex flex-col', className)}
        {...props}
      >
        {items.map((child, index) => (
          <React.Fragment key={React.isValidElement(child) ? (child.key ?? index) : index}>
            <div role="listitem">{child}</div>
            {dividers && index < items.length - 1 && (
              <div
                className="border-b border-border"
                data-testid="item-divider"
              />
            )}
          </React.Fragment>
        ))}
      </div>
    )
  }
)
IntuiItemGroup.displayName = 'IntuiItemGroup'

export { IntuiItem, IntuiItemGroup, intuiItemVariants }
